import { Image } from '@mantine/core';
import { styled } from '@mui/material/styles';
import { CATEGORIES } from 'navigation/routes';
import { getAllCategories } from 'network/services/goods';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Category } from 'TYPES/api.body';
import { COLORS } from '../../../theme/color';

const CategoriesMenuContainer = styled('div')`
  border-bottom: 1px solid ${COLORS.MainGray};
  margin-bottom: 1rem;
`;

const CategoryItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 1.1rem 0rem;
  text-decoration: none;
  color: ${COLORS.MainBlack};
  font-weight: 500;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 0 0.5rem 0.35rem 0.5rem;

  &:hover {
    background-color: ${COLORS.SecBlue};
    color: ${COLORS.MainBlue};
    transform: translateX(4px);
  }

  .category-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 1rem;
    background-color: ${COLORS.MildGray};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .category-name {
    flex: 1;
    font-size: 1.1rem;
  }
`;

const LoadingSkeleton = styled('div')`
  padding: 0.75rem 1.5rem;
  margin: 0 0.5rem 0.25rem 0.5rem;
  background-color: ${COLORS.MildGray};
  border-radius: 8px;
  height: 3rem;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

interface CategoriesMenuProps {
  onNavigate?: () => void;
}

const makeSlug = (name: string) => name.toLowerCase().split(' ').join('-');

// Add the fallback function here
const getCategoryFallbackImage = (categoryName: string): string => {
  const name = categoryName.toLowerCase();

  // Active categories
  if (name.includes('grocery') || name.includes('groceries') || name.includes('market')) {
    return '/images/categories/groceries.png';
  }
  if (name.includes('pharmacy') || name.includes('medical') || name.includes('health') || name.includes('medicine')) {
    return '/images/categories/pharmacy.png';
  }
  if (name.includes('restaurant') || name.includes('food') || name.includes('dining')) {
    return '/images/categories/restaurant.png';
  }
  if (
    name.includes('electronics') ||
    name.includes('gadget') ||
    name.includes('tech') ||
    name.includes('electrical') ||
    name.includes('accessories')
  ) {
    return '/images/categories/electronics.png';
  }
  if (name.includes('fashion') || name.includes('clothing') || name.includes('apparel')) {
    return '/images/categories/fashion.png';
  }
  if (name.includes('agriculture') || name.includes('farming')) {
    return '/images/categories/agriculture.png';
  }
  if (name.includes('home') || name.includes('furniture') || name.includes('decor')) {
    return '/images/categories/home.png';
  }
  if (name.includes('supermarket') || name.includes('market')) {
    return '/images/categories/supermarket.png';
  }
  if (name.includes('lottery') || name.includes('games') || name.includes('gym')) {
    return '/images/categories/lottery.png';
  }
  if (name.includes('automotive') || name.includes('car') || name.includes('vehicle')) {
    return '/images/categories/auto.png';
  }
  if (name.includes('event') || name.includes('occasion') || name.includes('party')) {
    return '/images/categories/events.png';
  }

  // Default fallback
  return '/images/categories/default.png';
};

const CategoriesMenu: React.FC<CategoriesMenuProps> = ({ onNavigate }) => {
  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories-menu'],
    queryFn: () => getAllCategories(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 2,
    retryDelay: 1000
  });

  const handleCategoryClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  if (isLoading) {
    return (
      <CategoriesMenuContainer>
        {[1, 2, 3, 4, 5].map(i => (
          <LoadingSkeleton key={i} />
        ))}
      </CategoriesMenuContainer>
    );
  }

  if (!categories || categories.length === 0) {
    return null;
  }

  // Only show the three main categories
  const shouldDisplayCategory = (categoryName: string): boolean => {
    const name = categoryName.toLowerCase();
    return (
      name.includes('grocery') ||
      name.includes('groceries') ||
      name.includes('market') ||
      name.includes('pharmacy') ||
      name.includes('medical') ||
      name.includes('health') ||
      name.includes('medicine') ||
      name.includes('electronics') ||
      name.includes('electrical') ||
      name.includes('accessories')
    );
  };
  const filteredCategories = categories.filter(category => shouldDisplayCategory(category.name));

  return (
    <CategoriesMenuContainer>
      {filteredCategories.map((category: Category) => (
        <CategoryItem
          key={category.id}
          to={`${CATEGORIES}/${makeSlug(category.name)}?category=${category.id}`}
          onClick={handleCategoryClick}>
          <div className="category-icon">
            <Image
              src={category.logo || getCategoryFallbackImage(category.name)}
              alt={category.name}
              onError={(e) => {
                // Additional fallback if image fails to load
                const target = e.target as HTMLImageElement;
                if (target.src !== getCategoryFallbackImage(category.name)) {
                  target.src = getCategoryFallbackImage(category.name);
                }
              }}
            />
          </div>
          <span className="category-name">{category.name}</span>
        </CategoryItem>
      ))}
    </CategoriesMenuContainer>
  );
};

export default CategoriesMenu;
